/**
 * Feature Flags Hook
 *
 * Fetches feature flags from /api/flags.
 * Shared state with 30-second TTL so flags stay fresh after toggles.
 * Call invalidateFlags() after toggling a flag to force immediate refresh.
 */

import { useState, useEffect } from 'react';

const CACHE_TTL_MS = 30_000;

let cachedFlags: Record<string, boolean> = {};
let cacheExpiresAt = 0;
let fetchPromise: Promise<void> | null = null;
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((cb) => cb());
}

async function doFetch() {
  try {
    const res = await fetch('/api/flags');
    if (res.ok) {
      cachedFlags = await res.json();
      cacheExpiresAt = Date.now() + CACHE_TTL_MS;
    }
  } catch {
    // Fail silently
  }
  fetchPromise = null;
  notifyListeners();
}

function ensureFresh() {
  if (Date.now() >= cacheExpiresAt && !fetchPromise) {
    fetchPromise = doFetch();
  }
}

/** Force an immediate refresh — call after toggling a flag. */
export function invalidateFlags() {
  cacheExpiresAt = 0;
  fetchPromise = doFetch();
  return fetchPromise;
}

export function useFlags(): Record<string, boolean> {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const listener = () => forceUpdate((n) => n + 1);
    listeners.add(listener);
    ensureFresh();
    return () => { listeners.delete(listener); };
  }, []);

  return cachedFlags;
}
